import React, { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link2, Plus, Trash2, Shield, RefreshCw, DollarSign, TrendingUp, TrendingDown, X, AlertTriangle, Server, Building2, KeyRound } from "lucide-react";

const REGIONS = ["new-york","london","singapore","chicago"];

export default function Broker() {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState({ account: null, positions: [], orders: [], loading: false });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/broker/connections");
      setConnections(data);
      if (data.length > 0 && !selectedId) setSelectedId(data[0].connection_id);
    } finally { setLoading(false); }
  }, [selectedId]);
  useEffect(() => { load(); }, [load]);

  const loadLive = async (cid) => {
    if (!cid) return;
    setLive({ account: null, positions: [], orders: [], loading: true });
    try {
      const [a, p, o] = await Promise.all([
        api.get(`/broker/connections/${cid}/account`),
        api.get(`/broker/connections/${cid}/positions`),
        api.get(`/broker/connections/${cid}/orders`),
      ]);
      setLive({ account: a.data, positions: p.data, orders: o.data, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broker sync failed");
      setLive(l => ({ ...l, loading: false }));
    }
  };
  useEffect(() => { loadLive(selectedId); }, [selectedId]);

  const toggleLive = async (cid, on) => {
    try {
      await api.patch(`/broker/connections/${cid}`, { live_trading_enabled: on });
      const updated = connections.map(c => c.connection_id === cid ? { ...c, live_trading_enabled: on } : c);
      setConnections(updated);
      toast[on ? "warning" : "success"](on ? "⚠ Live trading ENABLED. Real money at risk." : "Live trading disabled");
    } catch (e) { toast.error("Toggle failed"); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Remove this broker connection?")) return;
    await api.delete(`/broker/connections/${cid}`);
    setConnections(c => c.filter(x => x.connection_id !== cid));
    if (selectedId === cid) setSelectedId(null);
    toast.success("Connection removed");
  };

  const selected = connections.find(c => c.connection_id === selectedId);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="chip mb-3" data-testid="broker-eyebrow"><Link2 size={12}/> LIVE BROKER</div>
            <h1 className="font-display text-4xl text-white tracking-tight">Broker connections</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
              Connect your MetaAPI (works with any MT4/MT5 broker) or OANDA account to see real-time balance, open positions, and pending orders.
              <span className="text-[var(--warning)] font-semibold"> Live trading is opt-in per connection.</span>
            </p>
          </div>
          <button data-testid="add-connection-btn" onClick={()=>setShowAdd(true)} className="btn-primary flex items-center gap-2">
            <Plus size={14}/> Add connection
          </button>
        </div>

        {connections.length > 0 && (
          <div className="flex justify-end mb-4">
            <button data-testid="rotate-keys-btn" onClick={()=>setShowRotate(true)}
              className="btn-outline text-xs flex items-center gap-2">
              <KeyRound size={13}/> Rotate encryption key
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Loading…</p>
        ) : connections.length === 0 ? (
          <EmptyState onAdd={()=>setShowAdd(true)}/>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <aside className="lg:col-span-4 space-y-2">
              {connections.map(c => (
                <button key={c.connection_id}
                  data-testid={`connection-card-${c.connection_id}`}
                  onClick={()=>setSelectedId(c.connection_id)}
                  className={`w-full text-left card-sharp p-4 border transition-colors ${selectedId===c.connection_id ? "!border-[var(--accent)]" : ""}`}>
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-sm flex items-center justify-center text-[10px] font-mono font-bold uppercase
                      ${c.provider==="metaapi" ? "bg-[#6366F1]/20 text-[#818CF8]" : "bg-[#EF4444]/20 text-[#F87171]"}`}>
                      {c.provider==="metaapi" ? "MA" : "OA"}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-sm text-white">{c.label}</div>
                      <div className="text-[10px] font-mono text-[var(--text-muted)]">{c.provider} · {c.environment} · {c.account_id}</div>
                    </div>
                    {c.live_trading_enabled && <span className="chip chip-loss">◆ LIVE</span>}
                  </div>
                </button>
              ))}
            </aside>

            <section className="lg:col-span-8 space-y-4">
              {selected && (
                <>
                  <div className="card-sharp p-5" data-testid="connection-detail">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Building2 className="text-[var(--accent)]"/>
                      <div className="flex-1">
                        <div className="font-display text-2xl text-white">{selected.label}</div>
                        <div className="text-[11px] font-mono text-[var(--text-muted)]">
                          {selected.provider.toUpperCase()} · {selected.environment} · account #{selected.account_id} · token {selected.token_preview}
                        </div>
                      </div>
                      <button data-testid="refresh-live-btn" onClick={()=>loadLive(selected.connection_id)}
                        className="btn-outline flex items-center gap-2 text-sm !py-1.5"><RefreshCw size={13}/> Refresh</button>
                      <button data-testid="remove-connection-btn" onClick={()=>remove(selected.connection_id)}
                        className="p-2 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><Trash2 size={14}/></button>
                    </div>

                    <div className="mt-5 border-t border-white/5 pt-5 flex items-center gap-3">
                      <input data-testid="live-trading-toggle" type="checkbox" id="live-toggle"
                        checked={!!selected.live_trading_enabled}
                        onChange={(e)=>toggleLive(selected.connection_id, e.target.checked)}
                        className="accent-[var(--loss)] scale-125"/>
                      <label htmlFor="live-toggle" className="cursor-pointer flex-1">
                        <div className="font-display text-lg text-white flex items-center gap-2">
                          <AlertTriangle size={16} className="text-[var(--warning)]"/> Enable LIVE trading on this connection
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] mt-1">
                          When ON, MQL4Forge can place real orders on this broker. Requires Pro plan. You accept full responsibility for trading outcomes.
                        </div>
                      </label>
                    </div>
                  </div>

                  {live.loading ? (
                    <div className="card-sharp p-5">
                      <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Syncing…</p>
                    </div>
                  ) : live.account ? (
                    <>
                      <AccountSummary account={live.account}/>
                      <PositionsTable positions={live.positions}/>
                      <OrdersTable orders={live.orders}/>
                    </>
                  ) : null}
                </>
              )}
            </section>
          </div>
        )}
      </main>

      {showAdd && <AddConnectionModal onClose={()=>setShowAdd(false)} onAdded={(c)=>{
        setConnections(prev => [c, ...prev]);
        setSelectedId(c.connection_id);
        setShowAdd(false);
      }}/>}

      {showRotate && <RotateKeyModal onClose={()=>setShowRotate(false)}/>}
    </div>
  );
}

function RotateKeyModal({ onClose }) {
  const [oldKey, setOldKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const submit = async () => {
    if (!oldKey.trim()) { toast.error("Enter the OLD BROKER_TOKEN_KEY"); return; }
    if (!confirmed) { toast.error("Please confirm the safety check"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/broker/rotate-keys", { old_key: oldKey.trim() });
      toast.success(`Rotated ${data.rotated}/${data.total} connection(s)`);
      if (data.failed > 0) toast.warning(`${data.failed} could not be decrypted with that key.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="rotate-modal" onClick={onClose}>
      <div className="card-sharp p-6 max-w-lg w-full" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[var(--accent)]"/>
            <h3 className="font-display text-xl text-white">Rotate encryption key</h3>
          </div>
          <button data-testid="rotate-close" onClick={onClose}
            className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
        </div>

        <div className="border border-[var(--warning)]/30 bg-[var(--warning)]/5 rounded p-3 mb-4">
          <div className="flex gap-2 items-start">
            <AlertTriangle size={14} className="text-[var(--warning)] mt-0.5 shrink-0"/>
            <p className="text-xs font-mono text-[var(--warning)] leading-relaxed">
              Use this <strong>only if</strong> the server's <code>BROKER_TOKEN_KEY</code> was rotated and your existing broker tokens can no longer be decrypted. You must supply the <strong>OLD</strong> Fernet key that was in use when the tokens were originally encrypted.
            </p>
          </div>
        </div>

        <label className="label-tiny">Previous Fernet key</label>
        <input data-testid="rotate-oldkey-input" type="password"
          value={oldKey} onChange={(e)=>setOldKey(e.target.value)}
          placeholder="Fernet.generate_key() output (44 chars, base64url)"
          className="input-dark w-full font-mono !text-xs"/>

        <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="rotate-confirm">
          <input type="checkbox" checked={confirmed} onChange={(e)=>setConfirmed(e.target.checked)}
            className="mt-1"/>
          <span className="text-xs font-mono text-[var(--text-secondary)]">
            I understand this will re-encrypt every broker credential I own with the current server key. This is idempotent — running it again does nothing.
          </span>
        </label>

        <button data-testid="rotate-submit" onClick={submit} disabled={busy || !confirmed}
          className="btn-primary w-full mt-5 flex items-center justify-center gap-2">
          <KeyRound size={14}/> {busy ? "Rotating…" : "Rotate now"}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="card-sharp p-12 text-center" data-testid="broker-empty-state">
      <Server className="mx-auto text-[var(--text-muted)]" size={40}/>
      <h3 className="font-display text-2xl text-white mt-4">No broker connected yet</h3>
      <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
        Add your MetaAPI or OANDA credentials to see real-time account state — balance, equity, open positions, pending orders — synced from your broker.
      </p>
      <button onClick={onAdd} className="btn-primary mt-6 inline-flex items-center gap-2"><Plus size={14}/> Add connection</button>
    </div>
  );
}

function AccountSummary({ account }) {
  return (
    <div className="card-sharp p-5" data-testid="account-summary">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign size={16} className="text-[var(--profit)]"/>
        <h3 className="font-display text-lg text-white">Account</h3>
        <span className="chip">{account.provider}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Balance" value={`${(account.balance ?? 0).toFixed?.(2) ?? account.balance} ${account.currency || ""}`} testId="acct-balance"/>
        <Stat label="Equity"  value={`${(account.equity ?? 0).toFixed?.(2) ?? account.equity} ${account.currency || ""}`} testId="acct-equity"/>
        <Stat label="Margin"  value={`${(account.margin ?? 0).toFixed?.(2) ?? account.margin}`} testId="acct-margin"/>
        <Stat label="Free"    value={`${(account.free_margin ?? 0).toFixed?.(2) ?? account.free_margin}`} testId="acct-free"/>
      </div>
    </div>
  );
}

function PositionsTable({ positions }) {
  return (
    <div className="card-sharp p-5" data-testid="positions-table">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={16} className="text-[var(--accent)]"/>
        <h3 className="font-display text-lg text-white">Open positions</h3>
        <span className="chip">{positions.length}</span>
      </div>
      {positions.length === 0 ? (
        <p className="text-xs font-mono text-[var(--text-muted)] italic">No open positions.</p>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead className="text-[var(--text-muted)] uppercase tracking-widest">
              <tr><th className="text-left py-1">Symbol</th><th>Side</th><th>Volume</th><th>Open</th><th className="text-right">P&L</th></tr>
            </thead>
            <tbody>
              {positions.map((p, i) => (
                <tr key={p.id || p.ticket || `${p.symbol}-${p.open_price}-${i}`} className="border-t border-white/5" data-testid={`pos-row-${i}`}>
                  <td className="py-2 text-white">{p.symbol}</td>
                  <td className={p.type === "buy" || p.type === 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}>{p.type}</td>
                  <td>{p.volume}</td>
                  <td>{p.open_price}</td>
                  <td className={`text-right ${(p.profit || 0) >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>{Number(p.profit || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrdersTable({ orders }) {
  return (
    <div className="card-sharp p-5" data-testid="orders-table">
      <div className="flex items-center gap-2 mb-3">
        <TrendingDown size={16} className="text-[var(--accent)]"/>
        <h3 className="font-display text-lg text-white">Pending orders</h3>
        <span className="chip">{orders.length}</span>
      </div>
      {orders.length === 0 ? (
        <p className="text-xs font-mono text-[var(--text-muted)] italic">No pending orders.</p>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead className="text-[var(--text-muted)] uppercase tracking-widest">
              <tr><th className="text-left py-1">Symbol</th><th>Type</th><th>Volume</th><th className="text-right">Price</th></tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={o.id || o.ticket || `${o.symbol}-${o.open_price}-${i}`} className="border-t border-white/5" data-testid={`ord-row-${i}`}>
                  <td className="py-2 text-white">{o.symbol}</td>
                  <td>{o.type}</td>
                  <td>{o.volume}</td>
                  <td className="text-right">{o.open_price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="border border-white/10 rounded p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className="font-display text-2xl mt-1 text-white">{value}</div>
    </div>
  );
}

function AddConnectionModal({ onClose, onAdded }) {
  const [provider, setProvider] = useState("metaapi");
  const [label, setLabel] = useState("My broker");
  const [token, setToken] = useState("");
  const [accountId, setAccountId] = useState("");
  const [environment, setEnv] = useState("new-york");  // reset when provider changes
  const [busy, setBusy] = useState(false);

  const changeProvider = (p) => {
    setProvider(p);
    setEnv(p === "metaapi" ? "new-york" : "practice");
  };

  const submit = async () => {
    if (!token.trim() || !accountId.trim()) { toast.error("Token and account ID are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/broker/connections", {
        provider, label: label.trim() || `${provider} account`, token: token.trim(), account_id: accountId.trim(), environment,
      });
      toast.success("Connection added");
      onAdded(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add connection");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="add-connection-modal">
      <div className="card-sharp p-6 max-w-xl w-full" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-[var(--accent)]"/>
            <h3 className="font-display text-xl text-white">Add broker connection</h3>
          </div>
          <button data-testid="modal-close" onClick={onClose} className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
        </div>

        <label className="label-tiny">Provider</label>
        <div className="flex gap-2 mb-4" data-testid="provider-selector">
          <button data-testid="provider-metaapi" onClick={()=>changeProvider("metaapi")}
            className={`flex-1 border rounded p-3 text-left transition-colors ${provider==="metaapi" ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-white/10 hover:border-white/25"}`}>
            <div className="font-display text-sm text-white">MetaAPI.cloud</div>
            <div className="text-[10px] font-mono text-[var(--text-muted)]">Any MT4/MT5 broker</div>
          </button>
          <button data-testid="provider-oanda" onClick={()=>changeProvider("oanda")}
            className={`flex-1 border rounded p-3 text-left transition-colors ${provider==="oanda" ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-white/10 hover:border-white/25"}`}>
            <div className="font-display text-sm text-white">OANDA v20</div>
            <div className="text-[10px] font-mono text-[var(--text-muted)]">Direct REST API</div>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-tiny">Label (your reference)</label>
            <input data-testid="conn-label-input" value={label} onChange={(e)=>setLabel(e.target.value)} className="input-dark" placeholder="My live account"/>
          </div>
          <div>
            <label className="label-tiny">
              {provider === "metaapi" ? "MetaAPI Auth Token" : "OANDA Personal Access Token"}
            </label>
            <input data-testid="conn-token-input" type="password" value={token} onChange={(e)=>setToken(e.target.value)} className="input-dark"
              placeholder={provider === "metaapi" ? "eyJhbGciOi…" : "12345678900987654321-abc…"}/>
          </div>
          <div>
            <label className="label-tiny">
              {provider === "metaapi" ? "MetaAPI Account ID (UUID from your MetaAPI dashboard)" : "OANDA Account ID (e.g. 101-004-1234567-001)"}
            </label>
            <input data-testid="conn-account-input" value={accountId} onChange={(e)=>setAccountId(e.target.value)} className="input-dark"
              placeholder={provider === "metaapi" ? "550e8400-e29b-41d4-a716-446655440000" : "101-004-1234567-001"}/>
          </div>
          <div>
            <label className="label-tiny">{provider === "metaapi" ? "Region" : "Environment"}</label>
            <select data-testid="conn-env-select" value={environment} onChange={(e)=>setEnv(e.target.value)} className="input-dark">
              {provider === "metaapi"
                ? REGIONS.map(r => <option key={r} value={r}>{r}</option>)
                : (<>
                    <option value="practice">Practice (demo)</option>
                    <option value="live">Live (real money)</option>
                  </>)}
            </select>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11px] font-mono text-[var(--text-secondary)] p-3 border border-white/5 rounded bg-black/30">
          <Shield size={12} className="text-[var(--accent)] mt-0.5 shrink-0"/>
          Your token is stored in our database and used only to call the broker's API on your behalf. Live trading is disabled by default; enable it explicitly per connection.
        </div>

        <button data-testid="submit-connection-btn" onClick={submit} disabled={busy}
          className="btn-primary w-full mt-5 flex items-center justify-center gap-2">
          {busy ? "Verifying…" : "Add connection"}
        </button>
      </div>
    </div>
  );
}
